import { ReactNode, useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import Footer from './Footer';

interface LayoutProps {
  children: ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuthStore();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAdminDropdownOpen, setIsAdminDropdownOpen] = useState(false);
  const adminDropdownRef = useRef<HTMLDivElement>(null);

  const isAdmin = user?.role === 'ADMIN';

  // Close admin dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (adminDropdownRef.current && !adminDropdownRef.current.contains(event.target as Node)) {
        setIsAdminDropdownOpen(false);
      }
    };

    if (isAdminDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isAdminDropdownOpen]);

  const navigation = [
    { name: 'Dashboard', href: '/', current: location.pathname === '/' },
    { name: 'Decks', href: '/decks', current: location.pathname === '/decks' },
    { name: 'Lobby', href: '/lobby', current: location.pathname === '/lobby' },
  ];

  const adminMenuItems = [
    { name: 'Users', href: '/admin/users', current: location.pathname === '/admin/users' },
    { name: 'Games', href: '/admin/games', current: location.pathname === '/admin/games' },
    { name: 'Subscribers', href: '/admin/subscribers', current: location.pathname === '/admin/subscribers' },
  ];

  const handleLogout = () => {
    logout();
    window.location.href = 'https://neoncrowgames.com/';
  };

  return (
    <div className="min-h-screen text-zinc-200" style={{
      background: "linear-gradient(180deg, #0a0e1a 0%, #1a0e1a 50%, #0a1a0e 100%)"
    }}>
      {/* Navigation */}
      <header className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <Link to="/" className="shrink-0">
              <img 
                src="/neon-crow-logo.png" 
                alt="Neon Crow Games"
                className="h-16 sm:h-20 lg:h-24 w-auto transition-all duration-300"
                style={{
                  filter: "drop-shadow(0 0 15px rgba(0, 255, 255, 0.6))",
                }}
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex gap-6 xl:gap-8 items-center">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className="font-['Orbitron'] text-cyan-400 no-underline text-sm xl:text-base font-bold tracking-wider uppercase transition-all duration-300 hover:text-cyan-300 hover:drop-shadow-[0_0_20px_rgba(0,255,255,0.8)]"
                  style={{
                    textShadow: item.current ? "0 0 20px rgba(0, 255, 255, 0.8)" : "0 0 10px rgba(0, 255, 255, 0.5)",
                    opacity: item.current ? 1 : 0.8
                  }}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* Admin Dropdown */}
              {isAdmin && (
                <div className="relative" ref={adminDropdownRef}>
                  <button
                    onClick={() => setIsAdminDropdownOpen(!isAdminDropdownOpen)}
                    className="font-['Orbitron'] text-cyan-400 no-underline text-sm xl:text-base font-bold tracking-wider uppercase transition-all duration-300 hover:text-cyan-300 hover:drop-shadow-[0_0_20px_rgba(0,255,255,0.8)] flex items-center gap-1"
                    style={{
                      textShadow: adminMenuItems.some(item => item.current) ? "0 0 20px rgba(0, 255, 255, 0.8)" : "0 0 10px rgba(0, 255, 255, 0.5)",
                      opacity: adminMenuItems.some(item => item.current) ? 1 : 0.8
                    }}
                  >
                    Admin
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {isAdminDropdownOpen && (
                    <div className="absolute top-full left-0 mt-2 w-48 bg-zinc-900/95 border border-cyan-400/30 rounded-lg shadow-xl backdrop-blur-sm z-50">
                      {adminMenuItems.map((item) => (
                        <Link
                          key={item.name}
                          to={item.href}
                          onClick={() => setIsAdminDropdownOpen(false)}
                          className="block px-4 py-3 text-sm font-['Orbitron'] text-cyan-400 hover:text-cyan-300 hover:bg-cyan-400/10 transition-all duration-300 first:rounded-t-lg last:rounded-b-lg"
                          style={{
                            backgroundColor: item.current ? 'rgba(0, 255, 255, 0.1)' : 'transparent'
                          }}
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </nav>

            {/* Desktop User Menu */}
            <div className="hidden lg:flex items-center gap-4 xl:gap-6">
              <span className="font-['Inter'] text-sm text-zinc-300">
                Welcome, <Link 
                  to="/profile"
                  className="text-cyan-400 font-semibold no-underline hover:text-cyan-300 transition-colors duration-300"
                >
                  {user?.username}
                </Link>
              </span>
              <button
                onClick={handleLogout}
                className="font-['Orbitron'] px-4 py-2 border-2 border-cyan-400 rounded-lg bg-transparent text-cyan-400 text-xs font-bold tracking-wider uppercase cursor-pointer transition-all duration-300 hover:bg-cyan-400/10 hover:shadow-[0_0_25px_rgba(0,255,255,0.6)]"
                style={{
                  boxShadow: "0 0 15px rgba(0, 255, 255, 0.3)"
                }}
              >
                Logout
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-cyan-400 hover:text-cyan-300 transition-colors duration-300"
              aria-label="Toggle mobile menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {/* Mobile Navigation Menu */}
          {isMobileMenuOpen && (
            <div className="lg:hidden mt-4 p-4 bg-black/20 backdrop-blur-sm rounded-lg border border-white/10">
              <nav className="space-y-3">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block font-['Orbitron'] text-cyan-400 no-underline text-sm font-bold tracking-wider uppercase transition-all duration-300 hover:text-cyan-300 py-2 px-3 rounded hover:bg-white/5"
                    style={{
                      textShadow: item.current ? "0 0 20px rgba(0, 255, 255, 0.8)" : "0 0 10px rgba(0, 255, 255, 0.5)",
                      opacity: item.current ? 1 : 0.8
                    }}
                  >
                    {item.name}
                  </Link>
                ))}
                
                {/* Mobile Admin Menu */}
                {isAdmin && (
                  <>
                    <div className="pt-2 mt-2 border-t border-white/10">
                      <div className="font-['Orbitron'] text-cyan-400 text-sm font-bold tracking-wider uppercase mb-2 px-3">
                        Admin
                      </div>
                      {adminMenuItems.map((item) => (
                        <Link
                          key={item.name}
                          to={item.href}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="block font-['Orbitron'] text-cyan-400 no-underline text-sm font-bold tracking-wider uppercase transition-all duration-300 hover:text-cyan-300 py-2 px-6 rounded hover:bg-white/5"
                          style={{
                            textShadow: item.current ? "0 0 20px rgba(0, 255, 255, 0.8)" : "0 0 10px rgba(0, 255, 255, 0.5)",
                            opacity: item.current ? 1 : 0.8
                          }}
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  </>
                )}
              </nav>
              
              {/* Mobile User Info */}
              <div className="mt-4 pt-4 border-t border-white/10">
                <div className="flex items-center justify-between">
                  <span className="font-['Inter'] text-sm text-zinc-300">
                    Welcome, <Link 
                      to="/profile"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="text-cyan-400 font-semibold no-underline hover:text-cyan-300 transition-colors duration-300"
                    >
                      {user?.username}
                    </Link>
                  </span>
                  <button
                    onClick={handleLogout}
                    className="font-['Orbitron'] px-3 py-2 border border-cyan-400 rounded text-cyan-400 text-xs font-bold tracking-wider uppercase cursor-pointer transition-all duration-300 hover:bg-cyan-400/10"
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
        {children}
      </main>
      
      {/* Gradient line separator */}
      <div className="w-full h-1 bg-gradient-to-r from-pink-500 via-orange-500 via-yellow-500 via-green-500 to-cyan-500 shadow-[0_0_20px_rgba(0,255,255,0.5)]" />
      
      <Footer />
    </div>
  );
}
